import { Pagination, Table, TableData, TableHeader, TableRow } from '@/components/Grid'
import { Loading } from '@/components/Loading'
import { PageToolbar } from '@/containers/PageToolbar'
import { SearchButton } from '@/containers/search/SearchButton'
import {
  ErrorHandlingSagaPreset,
  errorSourceLabels,
  sagaEventLabels,
  type ErrorHandlingSagaGridItem,
  type ErrorHandlingSagaState,
} from '@/dtos/ErrorHandlingSaga'
import { useErrorsFilterContext } from '@/pages/ErrorsPage'
import { Badge, Box, Button, ButtonGroup, Card, CardBody, Checkbox, Flex, FormControl, HStack, Switch, Tbody, Tfoot, Thead } from '@chakra-ui/react'

import { FormLabel } from '@/components'
import { faArrowRotateRight, faHand } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Insurer } from 'BrokerTsLib/src/enums'
import { useMemo, useState } from 'react'
import { ErrorSearchForm } from './ErrorSearchForm'
import { ResolverDisplay } from './ResolverDisplay'
import { SagaActions } from './SagaActions'
import { SagaAssigneeSelect } from './SagaAssigneeSelect'

const presetTabs: { preset: ErrorHandlingSagaPreset; label: string }[] = [
  { preset: ErrorHandlingSagaPreset.Unassigned, label: 'Nepřiřazené chyby' },
  { preset: ErrorHandlingSagaPreset.Mine, label: 'Moje chyby' },
  { preset: ErrorHandlingSagaPreset.All, label: 'Všechny chyby' },
]

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString('cs-CZ') : '')
const insurerName = (id?: number) => (id != null ? ((Insurer as Record<number, string>)[id] ?? String(id)) : '')

// Display-only indicator (not clickable): retryable errors get the rotate icon, manual a plain label.
const ProcessingTypeBadge = ({ processingType }: { processingType: string }) => {
  const retryable = processingType === 'RETRYABLE'
  return (
    <Badge colorScheme={retryable ? 'blue' : 'gray'}
      title={retryable ? 'Retryable' : 'Manual'}
    >
      <FontAwesomeIcon icon={retryable ? faArrowRotateRight : faHand} />
    </Badge>
  )
}

const StateBadge = ({ state }: { state: ErrorHandlingSagaState }) => {
  const colorScheme = state === 'PENDING_START' || state === 'PENDING_RETRY' ? 'gray' : state === 'QUEUED' ? 'purple' : state === 'ON_HOLD' ? 'yellow' : state === 'RESOLVED' ? 'green' : state === 'CANCELED' ? 'orange' : 'red'

  return (
    <Badge colorScheme={colorScheme}>
      {state}
    </Badge>
  )
}

export const Errors = ({ errors }: { errors: ErrorHandlingSagaGridItem[] }) => {
  const { filter, updateFilter, currentPage, pageSize, totalCount, setCurrentPage, reload, loading } = useErrorsFilterContext()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [isSearchOpen, setSearchOpen] = useState(false)
  const [isSelectEnabled, setSelectEnabled] = useState(false)

  const ids = useMemo(() => errors.map(e => e.id), [errors])
  const allSelected = ids.length > 0 && ids.every(id => selected.has(id))

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(ids))

  const selectPreset = (preset: ErrorHandlingSagaPreset) => {
    setSelected(new Set())
    updateFilter(f => ({ ...f, preset, state: undefined, pageIndex: 1 }))
  }

  const afterAction = async () => {
    setSelected(new Set())
    await reload()
  }

  const noSelection = selected.size === 0

  return (
    <Box sx={{ display: 'grid', gap: 4, mb: 16 }}>
      <PageToolbar
        leftElements={
          <ButtonGroup>
            {presetTabs.map(t => (
              <Button
                key={t.preset}
                onClick={() => selectPreset(t.preset)}
                colorScheme={filter.preset === t.preset ? 'blue' : 'gray'}
                variant={filter.preset === t.preset ? 'solid' : 'outline'}
              >
                {t.label}
              </Button>
            ))}
          </ButtonGroup>
        }
        rightElements={<HStack gap={6}>
          <FormControl display='flex' alignItems='center'>
            <Switch isChecked={isSelectEnabled} onChange={(e) => setSelectEnabled(e.target.checked)} />
            <FormLabel variant={'switch'} fontSize={'sm'} fontWeight={'medium'} htmlFor='showWithErrors' cursor='pointer'>Hromadný výběr</FormLabel>
          </FormControl>
          <SearchButton isOpen={isSearchOpen} toggleOpen={() => setSearchOpen(v => !v)} />
        </HStack>}
      />

      {isSearchOpen && <ErrorSearchForm />}

      {isSelectEnabled &&
        <Flex gap={2} align="center">
          <SagaActions ids={[...selected]} onDone={afterAction} isDisabled={noSelection || loading} />
          <Box ml="auto" color="gray.500">Vybráno: {selected.size}</Box>
        </Flex>
      }

      <Card>
        <CardBody>
          <Table size="sm" cellPadding="0" cellSpacing="0">
            <Thead>
              <TableRow hoverEffect={false}>
                {isSelectEnabled
                  ? <TableHeader><Checkbox isChecked={allSelected} onChange={toggleAll} /></TableHeader>
                  : null
                }
                <TableHeader>Datum vytvoření</TableHeader>
                <TableHeader>Nadřazený proces</TableHeader>
                <TableHeader>Typ požadavku</TableHeader>
                <TableHeader>Číslo LS</TableHeader>
                <TableHeader>Číslo PS</TableHeader>
                <TableHeader>Pojišťovna</TableHeader>
                <TableHeader>Typ chyby</TableHeader>
                <TableHeader>Počet volání</TableHeader>
                <TableHeader>Datum posledního volání</TableHeader>
                <TableHeader>Typ</TableHeader>
                <TableHeader>Stav</TableHeader>
                <TableHeader>Řešitel</TableHeader>
                <TableHeader>Poznámka</TableHeader>
                <TableHeader>Vyřešil</TableHeader>
                <TableHeader></TableHeader>
              </TableRow>
            </Thead>
            <Tbody>
              {loading && (
                <TableRow hoverEffect={false}><TableData colSpan={16} py={8}><Loading large /></TableData></TableRow>
              )}
              {!loading && errors.map(e => (
                <TableRow key={e.id}>
                  {isSelectEnabled
                    ? <TableData><Checkbox isChecked={selected.has(e.id)} onChange={() => toggle(e.id)} /></TableData>
                    : null
                  }
                  <TableData whiteSpace="nowrap">{formatDate(e.createdAt)}</TableData>
                  <TableData>{e.eventType != null ? sagaEventLabels[e.eventType] : ''}</TableData>
                  <TableData>{e.eventType != null ? sagaEventLabels[e.eventType] : ''}</TableData>
                  <TableData>{e.leasingContractNumber}</TableData>
                  <TableData>{e.contractNumber}</TableData>
                  <TableData>{insurerName(e.insurerId)}</TableData>
                  <TableData>{e.errorSource != null ? errorSourceLabels[e.errorSource] : ''}</TableData>
                  <TableData textAlign="center">{e.attemptCount}</TableData>
                  <TableData whiteSpace="nowrap">{formatDate(e.lastAttemptAt)}</TableData>
                  <TableData textAlign="center"><ProcessingTypeBadge processingType={e.processingType} /></TableData>
                  <TableData><StateBadge state={e.state} /></TableData>
                  <TableData><SagaAssigneeSelect ids={[e.id]} assigneeId={e.assigneeId} assigneeName={e.assigneeName} onAssigned={afterAction} /></TableData>
                  <TableData maxW="160px" isTruncated title={e.note}>{e.note}</TableData>
                  <TableData><ResolverDisplay state={e.state} resolvedById={e.resolvedById} resolvedByName={e.resolvedByName} /></TableData>
                  <TableData>
                    <Button as="a" href={`Errors/Detail/${e.id}`} target="_blank" variant="outline" size="sm">Detail</Button>
                  </TableData>
                </TableRow>
              ))}
              {!loading && errors.length === 0 && (
                <TableRow hoverEffect={false}>
                  <TableData colSpan={16} py={8} textAlign="center" color="gray.500">Žádné chyby</TableData>
                </TableRow>
              )}
            </Tbody>
            {!loading && (
              <Tfoot>
                <TableRow hoverEffect={false}>
                  <TableData colSpan={16}>
                    <Pagination
                      totalCount={totalCount}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      maxButtonCount={10}
                      pageSize={pageSize}
                    />
                  </TableData>
                </TableRow>
              </Tfoot>
            )}
          </Table>
        </CardBody>
      </Card>
    </Box>
  )
}
